/**
 * Readiness for the open candidates (FR-SEL-015), under the throttle.
 *
 * The directory carries no readiness BY DESIGN; the contract says the UI reads
 * endpoint #5 per open candidate. The endpoint is throttled at 100 requests a
 * minute per caller, so: at most 10 reads per directory answer, each location
 * read at most once per answer, and an answer for a superseded directory
 * answer is dropped rather than shown against the wrong list.
 */

import { useEffect, useRef, useState } from "react";
import type { ApiClient } from "../api/client.js";
import { readReadiness } from "./locationsApi.js";
import type { Readiness } from "./types.js";

export const MAX_READINESS_READS = 10;

interface Held {
  readonly answerKey: string;
  readonly values: ReadonlyMap<string, Readiness | null>;
}

const NOTHING: ReadonlyMap<string, Readiness | null> = new Map<string, Readiness | null>();

/**
 * `answerKey` names the directory answer the targets came from ("" for none);
 * `targets` are the open candidates in display order.
 */
export function useReadiness(
  client: ApiClient,
  answerKey: string,
  targets: readonly string[],
  mode: string | null,
): ReadonlyMap<string, Readiness | null> {
  const [held, setHeld] = useState<Held>({ answerKey: "", values: NOTHING });
  const requested = useRef<{ answerKey: string; ids: Set<string> }>({ answerKey: "", ids: new Set() });
  const targetsKey = targets.join(",");

  useEffect(() => {
    if (requested.current.answerKey !== answerKey) {
      requested.current = { answerKey, ids: new Set() };
    }
    if (answerKey === "") return;
    const asked = requested.current.ids;
    const due = targetsKey
      .split(",")
      .filter((id) => id !== "" && !asked.has(id))
      .slice(0, Math.max(0, MAX_READINESS_READS - asked.size));
    for (const id of due) {
      asked.add(id);
      void readReadiness(client, id, mode).then((value) => {
        // Dropped when the list it was asked for has since been replaced.
        if (requested.current.answerKey !== answerKey) return;
        setHeld((previous) => ({
          answerKey,
          values: new Map(previous.answerKey === answerKey ? previous.values : []).set(id, value),
        }));
      });
    }
  }, [client, answerKey, targetsKey, mode]);

  return held.answerKey === answerKey ? held.values : NOTHING;
}
